/**
 * LIGHT module — owns every light source: cool hemisphere ambient + cold sky
 * fill, streetlamp pools with visible volumetric cones and wet-asphalt glow
 * smears, flickering neon, background window practicals, the clue accent
 * spot, office key spotlight with venetian gobo + visible shaft, desk lamp,
 * and the GPU rain streaks that live in the light pools.
 * Crossfades street ↔ office rigs on zone:changed; finale moods on
 * game:won / game:lost.
 * Owned by LIGHT; see CONTRACTS.md.
 */
import type { ModuleContext } from '../core/types'
import { createZoneBlend } from './blend'
import { LOST_GLOBAL_DIM } from './constants'
import { createNeonRig } from './neon'
import { createOfficeRig } from './office'
import { createRainRig } from './rain'
import { createStreetRig } from './street'
import { createVolumetricsRig } from './volumetrics'

const INITIAL_ZONE = 'outdoor' as const

export const initLighting = (ctx: ModuleContext): void => {
  const street = createStreetRig(ctx.scene)
  const office = createOfficeRig(ctx.scene)
  const neon = createNeonRig(ctx.scene)
  const volumetrics = createVolumetricsRig(ctx.scene, street.lampPositions)
  const rain = createRainRig(ctx.scene, [
    ...street.lampPositions,
    neon.position,
    ...street.accentPositions
  ])
  const blend = createZoneBlend(INITIAL_ZONE)

  ctx.bus.on('zone:changed', ({ zone }) => {
    blend.setZone(zone)
  })
  ctx.bus.on('game:won', () => {
    neon.setMode('steady')
  })
  ctx.bus.on('game:lost', () => {
    neon.setMode('dead')
    blend.setGlobalDim(LOST_GLOBAL_DIM)
  })

  ctx.loop.onUpdate((dt) => {
    blend.update(dt)
    const weight = blend.weight()
    const dim = blend.globalDim()
    street.applyBlend(weight, dim)
    office.applyBlend(weight, dim)
    neon.update(dt, weight, dim)
    volumetrics.applyBlend(weight, dim)
    rain.update(dt, weight, dim)
  })
}
